import { useQuery } from '@tanstack/react-query';
import { matchPath } from 'react-router';
import { registerPartnersQueries } from '@repo/api-kit/queries/register-partners';
import { registerRedirectionTokensQueries } from '@repo/api-kit/queries/register-redirection-tokens';
import { useEmberLocation } from '@repo/poly-hooks/routing/-ember/get-location';
import { useRegistration } from './use-registration';

// First path segments that are static routes, not a welcome redirection id.
const NON_REDIRECTION_SEGMENTS = new Set(['registrations', 'login', 'next-steps', 'react', '404']);

function getRedirectionTokenId(pathname: string, registrationId?: string): string | undefined {
  // On a registration URL the partner comes from the registration, not a token.
  if (registrationId) {
    return undefined;
  }

  const match = matchPath('/:redirectionId/*', pathname);
  const candidate = match?.params.redirectionId;

  if (!candidate || NON_REDIRECTION_SEGMENTS.has(candidate)) {
    return undefined;
  }

  return candidate;
}

/**
 * Resolves the partner name for the current onboarding. Prefers the
 * registration's own partner name (registration flow), then the standalone
 * partner registration lookup, then the redirection token's partner
 * (welcome flow).
 */
export function usePartnerName(): string | undefined {
  const { pathname } = useEmberLocation();
  const registrationId = matchPath('/registrations/:registrationId/*', pathname)?.params
    .registrationId;

  const redirectionTokenId = getRedirectionTokenId(pathname, registrationId);

  const { data } = useRegistration(registrationId);
  const { data: redirectionToken } = useQuery({
    ...registerRedirectionTokensQueries.detail(redirectionTokenId),
    enabled: Boolean(redirectionTokenId),
  });
  // Standalone query, independent of useRegistration's organization/membership
  // fetches — those can fail (e.g. no owner membership yet on an embed
  // registration) without the partner name lookup itself being at fault.
  const { data: partnerRegistration } = useQuery({
    ...registerPartnersQueries.partnerRegistration(registrationId ?? ''),
    enabled: Boolean(registrationId),
  });

  return (
    data?.registration?.partnerName ??
    partnerRegistration?.partnerRegistration?.partnerName ??
    redirectionToken?.partnerName
  );
}
